import { Info, Radio, ArrowRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { REDES_DISPONIBLES } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

type Props = {
  networks: string[];
  networkQueries: Record<string, string>;
  onChange: (networks: string[], queries: Record<string, string>) => void;
  onTraditionalClick?: () => void;
};

export function NetworkQueryEditor({ networks, networkQueries, onChange, onTraditionalClick }: Props) {
  const navigate = useNavigate();

  function goTraditional() {
    if (onTraditionalClick) onTraditionalClick();
    navigate({ to: "/media/campaigns/new" });
  }

  function toggle(code: string) {
    const next = networks.includes(code)
      ? networks.filter((n) => n !== code)
      : [...networks, code];
    const nextQueries = { ...networkQueries };
    if (!next.includes(code)) delete nextQueries[code];
    onChange(next, nextQueries);
  }

  function setQuery(code: string, q: string) {
    onChange(networks, { ...networkQueries, [code]: q });
  }

  return (
    <div className="space-y-3">
      <div>
        <Label>Redes a monitorear</Label>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {REDES_DISPONIBLES.map((r) => {
            const active = networks.includes(r.code);
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => toggle(r.code)}
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                  active
                    ? "border-brand-500 bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300"
                    : "border-ink-200 dark:border-ink-700 text-ink-600 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-800"
                }`}
              >
                <span>{r.label}</span>
                {!r.implementado && <Badge variant="muted">próx.</Badge>}
              </button>
            );
          })}
          {/* Entrypoint módulo Medios Tradicionales — abre wizard separado */}
          <button
            type="button"
            onClick={goTraditional}
            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-warn/40 text-sm transition text-warn hover:bg-orange-50/40 dark:hover:bg-orange-900/10 col-span-2 sm:col-span-1"
            title="Crear campaña en medios tradicionales (radio, TV, prensa)"
          >
            <span className="flex items-center gap-2">
              <Radio size={14} />
              Medios tradicionales
            </span>
            <ArrowRight size={12} />
          </button>
        </div>
        <p className="text-[10px] text-ink-400 mt-1">
          ¿Radio, TV o prensa escrita? El módulo de medios tradicionales gestiona campañas con cliente, evidencias y reportes.
        </p>
      </div>

      {networks.length > 0 && (
        <div className="space-y-3 border-t border-ink-200 dark:border-ink-800 pt-3">
          <div className="flex items-start gap-2 text-xs text-ink-500">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              Boolean query por red para filtrar resultados. Operadores: <code className="font-mono">AND</code>{" "}
              <code className="font-mono">OR</code> <code className="font-mono">NOT</code>{" "}
              <code className="font-mono">( )</code>, frases con comillas, <code className="font-mono">-</code> como NOT.
              Vacío = sin filtro.
            </span>
          </div>
          {networks.map((code) => {
            const meta = REDES_DISPONIBLES.find((r) => r.code === code);
            return (
              <div key={code}>
                <Label className="text-xs uppercase tracking-wide text-ink-500">{meta?.label ?? code}</Label>
                <textarea
                  className="mt-1 w-full min-h-[60px] rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 py-2 text-sm font-mono"
                  placeholder={`("aurora café" OR @auroracafe) AND NOT spam`}
                  value={networkQueries[code] ?? ""}
                  onChange={(e) => setQuery(code, e.target.value)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
